import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { ProjectRenderer, ProjectEntry } from '../../../models/Project';
import styled from '../../../theme';
import Text from './Text';
import OneHalf from './OneHalf';
import OneOne from './OneOne';
import ThreeOne from './ThreeOne';
import TwoOne from './TwoOne';
import requestIdleCallback from '../../../requestIdleCallback';

const Container = styled.div``;

const ItemContainer = styled.div`
  > * {
    margin-bottom: ${(props) => props.theme.mobilePadding}px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (min-width: ${(props) => props.theme.mobileBreakpoint}px) {
    > * {
      margin-bottom: 50px;

      > * {
        margin-right: 30px;

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }
`;

type IProps = {
  data: ProjectEntry;
  slug: string;
  index: number;
  archived: boolean;
} & RouteComponentProps<{ projectSlug: string; projectType: string }>;

interface IState {
  containerWidth: number;
}

function documentScrollTop() {
  return document.documentElement.scrollTop || document.body.scrollTop || 0;
}

class InnerContentContainer extends React.Component<IProps, IState> {
  state: IState = {
    containerWidth: 0,
  };
  private container: HTMLDivElement;

  private skipScroll: boolean = false;

  componentDidMount() {
    if (!this.canRender()) {
      // tslint:disable-next-line no-console
      console.warn(
        `Cannot render ${this.props.data.fields.name}. This project is missing ProjectContent.`
      );
      return;
    }
    setTimeout(() => {
      if (this.props.slug === this.props.match.params.projectSlug) {
        this.scrollToSelf();
      }
    }, 0);
    this.onResize();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll);
  }

  componentWillReceiveProps(nextProps: IProps) {
    if (!this.canRender()) {
      return;
    }
    if (
      nextProps.match.params.projectSlug !==
        this.props.match.params.projectSlug &&
      nextProps.match.params.projectSlug === nextProps.slug
    ) {
      if (this.skipScroll) {
        this.skipScroll = false;
        return;
      }
      this.scrollToSelf();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  }

  scrollToSelf = () => {
    const [entry] = this.getDimensions();
    window.scrollTo(0, entry);
  };

  determineActive = () => {
    const scrollTop = documentScrollTop();
    const [entry, exit] = this.getDimensions();
    const activeSlug = this.props.match.params.projectSlug;
    if (scrollTop >= entry && scrollTop < exit) {
      if (activeSlug !== this.props.slug) {
        this.skipScroll = true;
        this.props.history.replace(
          `${this.props.archived ? '/archive' : ''}/project/${
            this.props.match.params.projectType
          }/${this.props.slug}`
        );
      }
    } else if (this.props.index === 0 && scrollTop < entry) {
      if (undefined !== activeSlug) {
        this.props.history.replace(
          `${this.props.archived ? '/archive' : ''}/project/${
            this.props.match.params.projectType
          }`
        );
      }
    }
  };

  onResize = () => {
    requestIdleCallback(() => {
      if (this.container) {
        this.setState({ containerWidth: this.container.clientWidth });
      }
    });
  };

  onScroll = () => {
    requestIdleCallback(this.determineActive);
  };

  getDimensions = () => {
    const rect = this.container.getBoundingClientRect();
    return [
      rect.top + window.pageYOffset - 50,
      rect.top + window.pageYOffset + rect.height,
    ];
  };

  shouldComponentUpdate(nextProps: IProps, nextState: IState) {
    if (
      this.props.match.params.projectType !==
        nextProps.match.params.projectType ||
      this.state.containerWidth !== nextState.containerWidth
    ) {
      return true;
    }

    return false;
  }

  canRender = () => !!this.props.data.fields.ProjectContent;

  render() {
    if (!this.canRender()) {
      return null;
    }

    const { containerWidth } = this.state;
    return (
      <Container innerRef={(ref) => (this.container = ref)}>
        <ItemContainer>
          {this.props.data.fields.ProjectContent.map((projectContent) => {
            if (!projectContent.fields) {
              return null;
            }
            const type = projectContent.fields.type;

            if (type !== 'Text') {
              if (projectContent.fields.images.some((img) => !img.fields)) {
                return null;
              }
            }

            switch (type) {
              case '1 / 1':
                return (
                  <OneOne
                    key={projectContent.sys.id}
                    containerWidth={containerWidth}
                    projectContent={projectContent}
                  />
                );
              case '2 / 1':
                return (
                  <TwoOne
                    key={projectContent.sys.id}
                    containerWidth={containerWidth}
                    projectContent={projectContent}
                  />
                );
              case '1 / 2':
                return (
                  <OneHalf
                    key={projectContent.sys.id}
                    containerWidth={containerWidth}
                    projectContent={projectContent}
                  />
                );
              case '3 / 1':
                return (
                  <ThreeOne
                    key={projectContent.sys.id}
                    containerWidth={containerWidth}
                    projectContent={projectContent}
                  />
                );
              case 'Text':
                return (
                  <Text
                    key={projectContent.sys.id}
                    containerWidth={containerWidth}
                    projectContent={projectContent}
                  />
                );
              default:
                return null;
            }
          })}
        </ItemContainer>
      </Container>
    );
  }
}

interface IWrapperProps {
  projectSlug: string;
  index: number;
  archived: boolean;
}

export default withRouter(function Wrapper(
  props: IWrapperProps & RouteComponentProps<{}>
) {
  return (
    <ProjectRenderer
      index={props.index}
      slug={props.projectSlug}
      render={InnerContentContainer}
      match={props.match}
      history={props.history}
      archived={props.archived}
    />
  );
});



// WEBPACK FOOTER //
// ./src/scenes/Project/ProjectContent/index.tsx